import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { ISearchInput } from '../types';
import {
  IBriefUserInfoFieldsFragment,
  ISearchUsersQuery,
  ISearchUsersQueryVariables,
  UserState,
} from '../../../../utils/apollo/graphqlSchema';
import Avatar from '../../../../components/Avatar';
import { ReactSelectMenu } from '../../../../components/Dropdown/StyledReactSelect';
import { components, MultiValue, OptionProps } from 'react-select';
import { ControlWithSearchIcon } from '../../../../components/Dropdown/StyledReactSelect/SelectUtils';
import { closeMenuOnScrollOutside } from '../../../../components/Dropdown/StyledReactSelect/utils';
import AdvancedSearchMenuOption from './AdvancedSearchMenuOption';
import * as AdvancedSearchMenuUI from './AdvancedSearchMenuUI';
import { SEARCH_USERS_QUERY } from '../../../../utils/apollo/users';

interface IProps {
  searchInput: ISearchInput;
  setSearchInput: (searchInput: ISearchInput, isLoadMore: boolean) => void;
}

const MenuOption = (props: OptionProps<IBriefUserInfoFieldsFragment, true>) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <Avatar picture={data.picture} size={16} />
      <div>{data.name}</div>
    </components.Option>
  );
};

const CreatorSearchOption = ({ searchInput, setSearchInput }: IProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editors, setEditors] = useState<IBriefUserInfoFieldsFragment[]>([]);
  const onEditorFetch = (data: ISearchUsersQuery) => {
    setEditors(data.users.entities);
  };
  const { loading } = useQuery<ISearchUsersQuery, ISearchUsersQueryVariables>(
    SEARCH_USERS_QUERY,
    {
      variables: {
        term: searchTerm,
        includes: searchInput.editorIds,
        pagination: { first: 50, after: 0 },
        userStates: [UserState.Active, UserState.Pending],
      },
      onCompleted: onEditorFetch,
    },
  );
  const onRemoveSelectedUser = (removeUserId: string) => {
    setSearchInput(
      {
        ...searchInput,
        editorIds: searchInput.editorIds.filter(
          (editorId) => editorId !== removeUserId,
        ),
      },
      false,
    );
  };

  const onSelectUser = (
    newOptions: MultiValue<IBriefUserInfoFieldsFragment>,
  ) => {
    if (!newOptions) {
      return;
    }
    const newCreators = newOptions.map((option) => option.id);
    setSearchInput({ ...searchInput, editorIds: newCreators }, false);
  };

  return (
    <AdvancedSearchMenuUI.SectionContainer>
      <AdvancedSearchMenuUI.SectionHeading>
        Editor
      </AdvancedSearchMenuUI.SectionHeading>
      {searchInput.editorIds.length > 0 &&
        editors
          .filter((editor) => searchInput.editorIds.includes(editor.id))
          .map((editor) => {
            return (
              <AdvancedSearchMenuOption
                key={editor.id}
                onClick={() => onRemoveSelectedUser(editor.id)}
                checked={true}
              >
                <Avatar picture={editor.picture} size={16} />
                {editor.name}
              </AdvancedSearchMenuOption>
            );
          })}
      <ReactSelectMenu
        options={editors.filter((editor) => editor.name.length > 0)}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        onChange={onSelectUser}
        value={editors.filter((editor) =>
          searchInput.editorIds.includes(editor.id),
        )}
        isMulti={true}
        controlShouldRenderValue={false}
        hideSelectedOptions
        components={{
          Option: MenuOption,
          Control: ControlWithSearchIcon,
        }}
        isLoading={loading}
        isSearchable
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        placeholder={'Search for users'}
        menuPortalTarget={document.body}
        // close menu if scroll outside the menu
        closeMenuOnScroll={closeMenuOnScrollOutside}
        styles={{
          control: (base) => ({
            ...base,
            width: '100%',
            minWidth: 'unset',
          }),
          menu: (base) => ({
            ...base,
            width: '100%',
            minWidth: 'unset',
          }),
          option: (base) => ({
            ...base,
            display: 'flex',
            columnGap: '4px',
          }),
        }}
        blurInputOnSelect={true}
      />
    </AdvancedSearchMenuUI.SectionContainer>
  );
};
export default CreatorSearchOption;
